import React, { useEffect, useState } from 'react';
import AdminLayout from '../../components/layout/AdminLayout';
import api from '../../utils/api';
import toast from 'react-hot-toast';

const STATUS_BADGE = {
  growing:'badge--success', ready_to_sell:'badge--info',
  sold:'badge--warning', closed:'badge--gray'
};
const STATUS_LABEL = {
  growing:'Growing', ready_to_sell:'Ready to Sell', sold:'Sold', closed:'Closed'
};

export default function CropsPage() {
  const [crops,      setCrops]      = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [stats,      setStats]      = useState(null);
  const [total,      setTotal]      = useState(0);
  const [page,       setPage]       = useState(1);
  const [filters,    setFilters]    = useState({ status:'', category:'', subCategory:'', search:'' });
  const [editCrop,   setEditCrop]   = useState(null);
  const [newStatus,  setNewStatus]  = useState('');
  const [adminNote,  setAdminNote]  = useState('');
  const [saving,     setSaving]     = useState(false);
  const [exporting,  setExporting]  = useState(false);

  // ── Dynamic categories from DB ────────────────────────────
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    loadStats();
    loadCategories();
  }, []);

  useEffect(() => { loadCrops(); }, [filters, page]);

  const loadCategories = async () => {
    try {
      const { data } = await api.get('/categories');
      setCategories((data.categories || []).filter(c => c.isActive));
    } catch {}
  };

  const setF = (k, v) => { setFilters(p => ({ ...p, [k]: v })); setPage(1); };

  const loadStats = async () => {
    try {
      const { data } = await api.get('/admin/crops/stats');
      setStats(data.stats);
    } catch {}
  };

  const loadCrops = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit: 20 });
      Object.entries(filters).forEach(([k, v]) => { if (v) params.set(k, v); });
      const { data } = await api.get('/admin/crops?' + params);
      setCrops(data.crops || []); setTotal(data.total || 0);
    } catch { toast.error('Failed to load crops'); }
    finally { setLoading(false); }
  };

  const saveStatus = async () => {
    if (!editCrop) return;
    setSaving(true);
    try {
      await api.put('/admin/crops/' + editCrop._id + '/status', { status: newStatus, adminNote });
      setCrops(p => p.map(c => c._id === editCrop._id ? { ...c, status: newStatus } : c));
      toast.success('Status updated to ' + STATUS_LABEL[newStatus]);
      setEditCrop(null); loadStats();
    } catch { toast.error('Error'); }
    finally { setSaving(false); }
  };

  const deleteCrop = async (crop) => {
    if (!window.confirm('Delete ' + crop.cropName + '? Cannot be undone.')) return;
    try {
      await api.delete('/admin/crops/' + crop._id);
      setCrops(p => p.filter(c => c._id !== crop._id));
      setTotal(p => p - 1);
      toast.success('Crop deleted'); loadStats();
    } catch { toast.error('Error'); }
  };

  const exportCSV = async () => {
    setExporting(true);
    try {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([k, v]) => { if (v) params.set(k, v); });
      const { data } = await api.get('/admin/crops/export?' + params);
      if (!data.data?.length) { toast.error('No data to export'); return; }
      const headers = Object.keys(data.data[0]);
      const csv = [
        headers.join(','),
        ...data.data.map(r => headers.map(h => '"' + String(r[h] ?? '').replace(/"/g, '""') + '"').join(','))
      ].join('\r\n');
      const a = document.createElement('a');
      a.href = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
      a.download = 'crops-' + Date.now() + '.csv';
      a.click();
      toast.success('Exported ' + data.data.length + ' crops');
    } catch { toast.error('Export failed'); }
    finally { setExporting(false); }
  };

  const exportPDF = async () => {
    setExporting(true);
    try {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([k, v]) => { if (v) params.set(k, v); });
      const { data } = await api.get('/admin/crops/export?' + params);
      if (!data.data?.length) { toast.error('No data'); return; }
      const rows = data.data;
      const headerRow = Object.keys(rows[0]).map(h => '<th>' + h + '</th>').join('');
      const dataRows  = rows.map(r => '<tr>' + Object.values(r).map(v => '<td>' + String(v ?? '') + '</td>').join('') + '</tr>').join('');
      const html = `<html><head><title>Crop Report</title>
        <style>body{font-family:sans-serif;font-size:11px}table{width:100%;border-collapse:collapse}
        th{background:#1A6B3C;color:white;padding:5px 8px;text-align:left}
        td{padding:4px 8px;border-bottom:1px solid #eee}h2{color:#1A6B3C}
        @media print{button{display:none}}</style></head>
        <body><h2>Zeva2 Crop Report</h2>
        <p>Generated: ${new Date().toLocaleString('en-IN')} | Total: ${rows.length}</p>
        <table><tr>${headerRow}</tr>${dataRows}</table>
        <script>window.print()<\/script></body></html>`;
      const w = window.open('', '_blank');
      if (w) { w.document.write(html); w.document.close(); }
    } catch { toast.error('PDF failed'); }
    finally { setExporting(false); }
  };

  // Get subcategories for selected category
  const selectedCategory = categories.find(c =>
    c.name.toLowerCase() === filters.category.toLowerCase()
  );
  const subCategories = selectedCategory?.subCategories?.filter(s => s.isActive) || [];

  return (
    <AdminLayout title="Crop Management">
      {/* Stats */}
      {stats && (
        <div className="stat-grid" style={{ gridTemplateColumns:'repeat(5,1fr)', marginBottom:20 }}>
          {[
            { label:'Total',   value:stats.total,   icon:'', filter:'' },
            { label:'Growing', value:stats.growing, icon:'', filter:'growing' },
            { label:'Ready',   value:stats.ready,   icon:'🟢', filter:'ready_to_sell' },
            { label:'Sold',    value:stats.sold,    icon:'✅', filter:'sold' },
            { label:'Closed',  value:stats.closed,  icon:'🔒', filter:'closed' },
          ].map(s => (
            <div key={s.label} className="stat-card" style={{ cursor:'pointer' }}
              onClick={() => setF('status', s.filter)}>
              <div className="stat-card__icon">{s.icon}</div>
              <div className="stat-card__label">{s.label}</div>
              <div className="stat-card__value">{s.value}</div>
            </div>
          ))}
        </div>
      )}

      <div className="card" style={{ marginBottom:16 }}>
        <div className="card__body--sm">
          {/* Status pills */}
          <div className="pill-bar">
            {['', 'growing', 'ready_to_sell', 'sold', 'closed'].map(s => (
              <button key={s} className={'pill' + (filters.status === s ? ' pill--active' : '')}
                onClick={() => setF('status', s)}>
                {s ? STATUS_LABEL[s] : 'All Status'}
              </button>
            ))}
          </div>

          {/* ── Dynamic category pills from DB ── */}
          <div className="pill-bar">
            <button className={'pill' + (!filters.category ? ' pill--active' : '')}
              onClick={() => { setF('category', ''); setF('subCategory', ''); }}>
              All
            </button>
            {categories.map(c => (
              <button key={c._id}
                className={'pill' + (filters.category === c.name.toLowerCase() ? ' pill--active' : '')}
                onClick={() => { setF('category', c.name.toLowerCase()); setF('subCategory', ''); }}>
                {c.icon} {c.name}
              </button>
            ))}
          </div>

          {/* ── Dynamic subcategory pills ── */}
          {filters.category && subCategories.length > 0 && (
            <div className="pill-bar">
              <button className={'pill' + (!filters.subCategory ? ' pill--active' : '')}
                onClick={() => setF('subCategory', '')}>All</button>
              {subCategories.map(s => (
                <button key={s._id || s.name}
                  className={'pill' + (filters.subCategory === s.name ? ' pill--active' : '')}
                  onClick={() => setF('subCategory', s.name)}>
                  {s.icon} {s.name}
                </button>
              ))}
            </div>
          )}

          {/* Search + export */}
          <div style={{ display:'flex', gap:10, alignItems:'center' }}>
            <input className="input" placeholder="🔍 Search crop name..." value={filters.search}
              onChange={e => setF('search', e.target.value)} style={{ flex:1 }} />
            <button className="btn btn--outline btn--sm" onClick={exportCSV} disabled={exporting}>📊 CSV</button>
            <button className="btn btn--outline btn--sm" onClick={exportPDF} disabled={exporting}>📄 PDF</button>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="card">
        {loading ? <div className="page-loader"><span className="spinner spinner--md" /></div> : (
          <div className="table-wrap">
            <table>
              <thead><tr>
                <th>Crop</th><th>Farmer</th><th>Location</th><th>Status</th>
                <th>Day</th><th>Price</th><th>Likes</th><th>Added</th><th>Actions</th>
              </tr></thead>
              <tbody>
                {crops.length === 0 ? (
                  <tr><td colSpan={9}>
                    <div className="empty-state">
                      <div className="empty-state__icon"></div>
                      <div className="empty-state__title">No crops found</div>
                    </div>
                  </td></tr>
                ) : crops.map(crop => (
                  <tr key={crop._id}>
                    <td>
                      <div style={{ fontWeight:600 }}>{crop.cropName}</div>
                      <div style={{ fontSize:11, color:'#6C757D', textTransform:'capitalize' }}>
                        {crop.category} · {crop.subCategory}
                      </div>
                    </td>
                    <td>
                      <div style={{ fontSize:13, fontWeight:500 }}>{crop.userId?.name || '—'}</div>
                      <div style={{ fontSize:11, color:'#2563EB' }}>{crop.userId?.mobile}</div>
                    </td>
                    <td style={{ fontSize:12, color:'#6C757D' }}>
                      {(crop.userId?.locationAddress || '—').split(',')[0]}
                    </td>
                    <td>
                      <span className={'badge ' + (STATUS_BADGE[crop.status] || 'badge--gray')}>
                        {STATUS_LABEL[crop.status] || crop.status}
                      </span>
                    </td>
                    <td style={{ textAlign:'center' }}>D{crop.currentDayCount || 0}</td>
                    <td>{crop.price ? '₹' + crop.price : '—'}</td>
                    <td style={{ textAlign:'center' }}>{crop.likeCount || 0}</td>
                    <td style={{ fontSize:12 }}>{new Date(crop.createdAt).toLocaleDateString('en-IN')}</td>
                    <td>
                      <div style={{ display:'flex', gap:6 }}>
                        <button className="btn btn--primary btn--sm"
                          onClick={() => { setEditCrop(crop); setNewStatus(crop.status); setAdminNote(''); }}>
                          🔄
                        </button>
                        <button className="btn btn--danger btn--sm" onClick={() => deleteCrop(crop)}>🗑</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Pagination */}
      {total > 20 && (
        <div className="pagination">
          <button className="btn btn--outline btn--sm" disabled={page===1} onClick={() => setPage(p=>p-1)}>← Prev</button>
          <span className="page-info">Page {page} of {Math.ceil(total/20)} · {total} total</span>
          <button className="btn btn--outline btn--sm" disabled={page>=Math.ceil(total/20)} onClick={() => setPage(p=>p+1)}>Next →</button>
        </div>
      )}

      {/* Edit status modal */}
      {editCrop && (
        <div className="modal-overlay" onClick={() => setEditCrop(null)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal__header">
              <div className="modal__title">🔄 Change Status — {editCrop.cropName}</div>
              <button className="modal__close" onClick={() => setEditCrop(null)}>×</button>
            </div>
            <div className="modal__body">
              <div style={{ fontSize:12, color:'#6C757D', marginBottom:12 }}>
                Farmer: {editCrop.userId?.name} · +91 {editCrop.userId?.mobile}
              </div>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:8, marginBottom:14 }}>
                {[
                  { v:'growing',       label:' Growing',       desc:'Crop actively growing' },
                  { v:'ready_to_sell', label:'🟢 Ready to Sell', desc:'Available for buyers' },
                  { v:'sold',          label:'✅ Sold',           desc:'Crop has been sold' },
                  { v:'closed',        label:'🔒 Closed',         desc:'Close on behalf of farmer' },
                ].map(s => (
                  <button key={s.v} onClick={() => setNewStatus(s.v)}
                    style={{ padding:'10px 12px', borderRadius:8,
                      border:'2px solid ' + (newStatus===s.v ? '#1A6B3C' : '#dee2e6'),
                      background: newStatus===s.v ? '#E8F5EE' : 'white',
                      textAlign:'left', cursor:'pointer' }}>
                    <div style={{ fontSize:13, fontWeight:600 }}>{s.label}</div>
                    <div style={{ fontSize:11, color:'#6C757D', marginTop:2 }}>{s.desc}</div>
                  </button>
                ))}
              </div>
              <div className="form-group">
                <label className="label">Admin Note (optional)</label>
                <textarea className="input" rows={2} value={adminNote}
                  onChange={e => setAdminNote(e.target.value)}
                  placeholder="Reason for change..." />
              </div>
            </div>
            <div className="modal__footer">
              <button className="btn btn--outline" onClick={() => setEditCrop(null)}>Cancel</button>
              <button className="btn btn--primary" onClick={saveStatus} disabled={saving}>
                {saving ? <span className="spinner spinner--sm spinner--white" /> : '✅ Save Status'}
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}